import { DateTime } from "luxon";
import roleConfig from "../../../common/layout/roleConfig";
import { liste } from "../../ReglesSousOrientation/reglesSousOrientation";

// ============================================================
// Textes et contrôles de l'encart « Accompagnement » du dossier (CE-1370), et de
// l'orientation manuelle vers un opérateur (CE-1414), purs pour être testés
// seuls. Les raisons viennent de l'API, telles quelles.
// ============================================================

const FUSEAU = "Indian/Reunion";
const CAF = "caf";

const jour = (iso) => DateTime.fromISO(iso).setZone(FUSEAU).toFormat("dd/MM/yyyy");

/** « du Nord », « de l'Est ». */
const duTerritoire = (territoire) => (/^[AEIOUÉ]/.test(territoire.libelle) ? `de l'${territoire.libelle}` : `du ${territoire.libelle}`);

/** « dans le Nord », « dans l'Est ». */
export const dansLeTerritoire = (territoire) => (/^[AEIOUÉ]/.test(territoire.libelle) ? `dans l'${territoire.libelle}` : `dans le ${territoire.libelle}`);

const nom = (operateur) => (operateur ? operateur.nom : "Caf");

/** « 1 mesure restante », « 12 531 mesures restantes ». */
const mesuresRestantes = (n) => `${n.toLocaleString("fr-FR")} ${n > 1 ? "mesures restantes" : "mesure restante"}`;

const derniereCorrection = (so) => so.corrections[so.corrections.length - 1] || null;

const estManuelle = (so) => so.origine === "MANUELLE";

/** Les deux accompagnements qu'un agent choisit, dans l'ordre de la maquette 12. */
export const ACCOMPAGNEMENTS = [
  {
    valeur: "PARTIEL",
    titre: "Accompagnement partiel",
    description: "Le volet social reste à la Caf, le volet professionnel va à l'opérateur. Le référent reste à la Caf.",
  },
  { valeur: "TOTAL", titre: "Accompagnement total", description: "L'opérateur accompagne l'allocataire sur tout son parcours." },
];
const COURT = { TOTAL: "total", PARTIEL: "partiel" };

/** L'accompagnement d'aujourd'hui ; une réponse d'avant CE-1414 ne le donne pas : total chez un opérateur. */
const accompagnementDe = (so) => so.accompagnement || (so.operateur ? "TOTAL" : null);

/** À qui l'allocataire est confié aujourd'hui. */
export const nomAccompagnement = (so) => nom(so.operateur);

/** « Accompagnement partiel », « Accompagnement total » ; `null` à la Caf. */
export function libelleAccompagnement(so) {
  const accompagnement = accompagnementDe(so);
  const trouve = ACCOMPAGNEMENTS.find((a) => a.valeur === accompagnement);
  return trouve ? trouve.titre : null;
}

export function ligneAccompagnement(so) {
  const correction = derniereCorrection(so);
  if (correction) {
    return `Modifiée par ${correction.auteur.nom || "un agent"} le ${jour(correction.le)}.`;
  }
  if (estManuelle(so)) {
    const auteur = (so.orienteePar && so.orienteePar.nom) || "un agent";
    return `Orientation par ${auteur} le ${jour(so.decideeLe)}${so.accompagnement === "PARTIEL" ? ". Le référent reste à la Caf" : ""}.`;
  }
  const { decision } = so;
  if (decision.operateur && decision.regle) {
    const repartie = decision.repartition && decision.repartition.length > 1
      ? `, répartie entre ${liste(decision.repartition.map((r) => r.operateur.nom), "et")}`
      : "";
    return `Règle ${decision.regle.rang}, accompagnement total, quota ${decision.financement} ${duTerritoire(decision.territoire)} ${so.annee}${repartie}.`;
  }
  if (!so.version) {
    return "Aucune règle de sous-orientation n'était en vigueur.";
  }
  const passee = so.detail.find((d) => d.resultat === "PASSEE");
  return passee ? `Règle ${passee.rang} passée : ${passee.raison}.` : "Aucune règle ne s'applique.";
}

/** Chaque règle examinée, dans l'ordre, et la Caf quand aucune ne s'est appliquée ; rien pour une orientation manuelle. */
export function etapesDecision(so) {
  if (estManuelle(so)) {
    return [];
  }
  if (!so.version) {
    return [{ etat: "caf", texte: "Aucune version des règles n'était en vigueur : reste à la Caf" }];
  }
  const etapes = so.detail.map((d) => {
    switch (d.resultat) {
      case "APPLIQUEE":
        return { etat: "ok", texte: `Règle ${d.rang} : appliquée, vers ${nom(so.decision.operateur)}` };
      case "PASSEE":
        return { etat: "partiel", texte: `Règle ${d.rang} : passée, ${d.raison}` };
      case "NE_S_APPLIQUE_PAS":
        return { etat: "na", texte: `Règle ${d.rang} : ne s'applique pas, ${d.raison}` };
      default:
        return { etat: "avenir", texte: `Règle ${d.rang} : non examinée` };
    }
  });
  if (!so.detail.some((d) => d.resultat === "APPLIQUEE")) {
    etapes.push({ etat: "caf", texte: "Aucune règle appliquée : reste à la Caf" });
  }
  return etapes;
}

/**
 * « 12/11/2026, Leila Gangat : FTM → Caf. Motif : « … » », de la plus ancienne à la plus récente ;
 * l'accompagnement de chaque côté quand il change chez un opérateur (« FTM, partiel → FTM, total »).
 */
export const lignesCorrections = (so) =>
  so.corrections.map((c) => {
    const change = c.de && c.vers && c.accompagnementAvant && c.accompagnementApres && c.accompagnementAvant !== c.accompagnementApres;
    const cote = (operateur, accompagnement) => `${nom(operateur)}${change ? `, ${COURT[accompagnement]}` : ""}`;
    return `${jour(c.le)}, ${c.auteur.nom || "un agent"} : ${cote(c.de, c.accompagnementAvant)} → ${cote(c.vers, c.accompagnementApres)}. Motif : « ${c.motif} »`;
  });

/**
 * Pour l'accompagnement retenu : l'actuel, désactivé, s'il est dans cet accompagnement ; puis la Caf et les
 * opérateurs que l'API propose pour cet accompagnement (une réponse d'avant CE-1414 : total seulement).
 */
export function optionsCorrection(so, accompagnement = "TOTAL") {
  const dansCetAccompagnement = !so.operateur || accompagnementDe(so) === accompagnement;
  const actuel = { valeur: so.operateur ? so.operateur.id : CAF, titre: nom(so.operateur), description: "Accompagnement actuel", actuel: true };
  const choix = (so.choix || [])
    .filter((c) => !c.operateur || (c.accompagnements || ["TOTAL"]).includes(accompagnement))
    .map((c) =>
      c.operateur
        ? {
            valeur: c.operateur.id,
            titre: c.operateur.nom,
            description: `Accompagnement ${COURT[accompagnement]} · ${mesuresRestantes(c.restantes)} sur le quota ${so.decision.financement} ${duTerritoire(so.decision.territoire)} ${so.annee}`,
            actuel: false,
          }
        : { valeur: CAF, titre: "Caf", description: "Avec un travailleur social de la Caf", actuel: false }
    );
  return dansCetAccompagnement ? [actuel, ...choix.filter((c) => c.valeur !== actuel.valeur)] : choix;
}

/** Les erreurs de l'écran, sous leur champ. */
export function erreursCorrection({ vers, motif }) {
  const erreurs = {};
  if (!vers) erreurs.vers = "Choisissez à qui confier l'allocataire.";
  if (!(motif || "").trim()) erreurs.motif = "Indiquez le motif de la modification.";
  return erreurs;
}

/** L'accompagnement n'est envoyé que pour une orientation manuelle, qui peut en changer. */
export const corpsCorrection = (vers, motif, accompagnement = null) => ({
  operateurId: vers === CAF ? null : vers,
  motif: motif.trim(),
  ...(accompagnement ? { accompagnement } : {}),
});

/** Les opérateurs que l'API propose pour l'accompagnement choisi ; aucun avant ce choix (CE-1414). */
export function optionsOrientation(orientation, accompagnement) {
  if (!accompagnement || !orientation.territoire) return [];
  return orientation.choix
    .filter((c) => c.accompagnements.includes(accompagnement))
    .map((c) => ({
      valeur: c.operateur.id,
      titre: c.operateur.nom,
      description: `${mesuresRestantes(c.restantes)} sur le quota FSE+ ${duTerritoire(orientation.territoire)} ${orientation.annee}`,
    }));
}

/** Les erreurs de l'écran d'orientation, sous leur champ. */
export function erreursOrientation({ accompagnement, operateur }) {
  const erreurs = {};
  if (!accompagnement) erreurs.accompagnement = "Choisissez l'accompagnement : partiel ou total.";
  if (!operateur) erreurs.operateur = "Choisissez l'opérateur qui accompagnera l'allocataire.";
  return erreurs;
}

/** Pourquoi un allocataire orientable ne peut être confié à personne ; `null` s'il le peut. */
export function ligneOrientationImpossible(orientation) {
  if (!orientation.territoire) return "Commune inconnue : l'allocataire ne peut pas être confié à un opérateur.";
  if (orientation.choix.length === 0) {
    return `Aucun opérateur ne peut recevoir l'allocataire ${dansLeTerritoire(orientation.territoire)} : quotas atteints ou non saisis.`;
  }
  return null;
}

/** Seulement pour un allocataire dont la dernière orientation est la Caf, et un rôle qui lit la sous-orientation. */
export function afficheAccompagnement(recipient, role) {
  const orientation = recipient && recipient.lastOrientation && recipient.lastOrientation.orientation;
  return orientation === "CAF" && !((role && roleConfig[role]) || []).includes("view_sous_orientation");
}
